import React from 'react';
import { useNavigate } from 'react-router-dom';

const css = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: oklch(99% 0.003 260);
  --bg-2: oklch(96.5% 0.007 260);
  --bg-3: oklch(93% 0.01 260);
  --border: oklch(89% 0.01 260);
  --border-strong: oklch(80% 0.015 260);
  --text: oklch(14% 0.015 260);
  --text-2: oklch(40% 0.02 260);
  --text-3: oklch(60% 0.015 260);
  --accent: oklch(52% 0.22 265);
  --accent-mid: oklch(62% 0.18 265);
  --accent-light: oklch(95.5% 0.055 265);
  --teal: oklch(55% 0.15 195);
  --green: oklch(55% 0.15 145);
  --amber: oklch(68% 0.16 75);
  --radius: 8px;
  --radius-lg: 14px;
}
html { scroll-behavior: smooth; }
body { font-family: 'Inter', sans-serif; background: var(--bg); color: var(--text); font-size: 15px; line-height: 1.6; -webkit-font-smoothing: antialiased; }
a { color: inherit; text-decoration: none; }
img { display: block; max-width: 100%; }

.container { max-width: 1120px; margin: 0 auto; padding: 0 32px; }

nav {
  position: sticky; top: 0; z-index: 50;
  background: oklch(99% 0.003 260 / 0.85);
  backdrop-filter: blur(16px) saturate(150%);
  border-bottom: 1px solid var(--border);
}
.nav-inner { display: flex; align-items: center; justify-content: space-between; height: 60px; gap: 32px; }
.nav-logo { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.nav-logo-mark { width: 30px; height: 30px; border-radius: 7px; background: var(--accent); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.nav-logo-name { font-weight: 700; font-size: 15px; letter-spacing: -0.3px; color: var(--text); }
.nav-links { display: flex; align-items: center; gap: 4px; }
.nav-link { padding: 6px 14px; border-radius: 6px; font-size: 13.5px; font-weight: 500; color: var(--text-2); transition: background 0.12s, color 0.12s; cursor: pointer; }
.nav-link:hover { background: var(--bg-2); color: var(--text); }
.nav-actions { display: flex; align-items: center; gap: 8px; }
.btn-ghost { padding: 7px 16px; border-radius: 6px; font-size: 13.5px; font-weight: 600; color: var(--text-2); border: none; background: transparent; cursor: pointer; font-family: inherit; transition: color 0.12s; }
.btn-ghost:hover { color: var(--text); }
.btn-primary { padding: 8px 18px; border-radius: 6px; font-size: 13.5px; font-weight: 600; color: #fff; background: var(--accent); border: none; cursor: pointer; font-family: inherit; transition: background 0.15s; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary:hover { background: oklch(46% 0.22 265); }
.btn-primary-lg { padding: 13px 28px; font-size: 15px; border-radius: 8px; }
.btn-secondary-lg { padding: 12px 28px; font-size: 15px; border-radius: 8px; background: #fff; color: var(--text); border: 1.5px solid var(--border-strong); font-weight: 600; font-family: inherit; cursor: pointer; transition: border-color 0.15s, background 0.15s; display: inline-flex; align-items: center; gap: 6px; }
.btn-secondary-lg:hover { border-color: var(--accent); background: var(--accent-light); color: var(--accent); }

.hero { padding: 100px 0 80px; text-align: center; position: relative; overflow: hidden; }
.hero-eyebrow { display: inline-flex; align-items: center; gap: 7px; padding: 5px 14px; background: var(--accent-light); border: 1px solid oklch(87% 0.09 265); border-radius: 99px; font-size: 12.5px; font-weight: 600; color: var(--accent); margin-bottom: 28px; letter-spacing: 0.2px; }
.hero-eyebrow-dot { width: 6px; height: 6px; border-radius: 99px; background: var(--accent); animation: pulse 2s infinite; }
@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.85)} }
.hero h1 { font-size: clamp(38px, 5.5vw, 66px); font-weight: 800; letter-spacing: -2px; line-height: 1.06; color: var(--text); max-width: 800px; margin: 0 auto 24px; text-wrap: balance; }
.hero h1 em { font-style: normal; color: var(--accent); }
.hero-sub { font-size: clamp(16px, 2vw, 19px); color: var(--text-3); max-width: 560px; margin: 0 auto 40px; font-weight: 400; line-height: 1.65; text-wrap: pretty; }
.hero-actions { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
.hero-social { margin-top: 48px; display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 12.5px; color: var(--text-3); }
.hero-avatars { display: flex; }
.hero-avatar { width: 28px; height: 28px; border-radius: 99px; border: 2px solid var(--bg); margin-left: -8px; font-size: 11px; font-weight: 700; color: #fff; display: flex; align-items: center; justify-content: center; }

.ide-preview { max-width: 900px; margin: 60px auto 0; position: relative; }
.ide-preview-glow { position: absolute; inset: -60px; background: radial-gradient(ellipse at 50% 40%, oklch(52% 0.22 265 / 0.12) 0%, transparent 70%); pointer-events: none; z-index: 0; }
.ide-window { position: relative; z-index: 1; background: oklch(13% 0.02 260); border: 1px solid oklch(25% 0.02 260); border-radius: var(--radius-lg); box-shadow: 0 32px 80px oklch(0% 0 0 / 0.28), 0 4px 16px oklch(0% 0 0 / 0.15); overflow: hidden; }
.ide-titlebar { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: oklch(16% 0.02 260); border-bottom: 1px solid oklch(22% 0.02 260); }
.ide-dot { width: 12px; height: 12px; border-radius: 99px; }
.ide-tab { padding: 4px 14px; border-radius: 4px; font-size: 12px; font-family: 'JetBrains Mono', monospace; color: oklch(65% 0.02 260); background: oklch(20% 0.02 260); margin-left: 8px; }
.ide-tab.active { background: oklch(24% 0.03 265); color: oklch(85% 0.05 265); }
.ide-body { display: grid; grid-template-columns: 1fr 1fr; min-height: 340px; }
.ide-code { padding: 24px; border-right: 1px solid oklch(20% 0.02 260); font-family: 'JetBrains Mono', monospace; font-size: 12.5px; line-height: 1.8; color: oklch(80% 0.02 260); overflow: hidden; }
.ide-panel { padding: 20px; display: flex; flex-direction: column; gap: 0; }
.ide-panel-head { font-size: 11px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: oklch(45% 0.02 260); padding-bottom: 12px; border-bottom: 1px solid oklch(20% 0.02 260); margin-bottom: 16px; }
.metric-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid oklch(18% 0.01 260); gap: 12px; }
.metric-label { font-size: 12px; color: oklch(60% 0.02 260); font-family: 'JetBrains Mono', monospace; }
.metric-bar-wrap { flex: 1; height: 4px; background: oklch(22% 0.02 260); border-radius: 99px; overflow: hidden; }
.metric-bar { height: 100%; border-radius: 99px; }
.metric-val { font-size: 12px; font-family: 'JetBrains Mono', monospace; font-weight: 500; min-width: 28px; text-align: right; }
.ide-score { margin-top: 16px; padding: 14px; background: oklch(17% 0.02 260); border-radius: 8px; display: flex; align-items: center; justify-content: space-between; }
.ide-score-num { font-family: 'JetBrains Mono', monospace; font-size: 32px; font-weight: 700; color: oklch(75% 0.15 145); line-height: 1; }
.ide-score-label { font-size: 11px; color: oklch(45% 0.02 260); font-weight: 600; letter-spacing: 0.3px; }
.ide-badge { padding: 4px 10px; border-radius: 99px; font-size: 11px; font-weight: 700; background: oklch(22% 0.12 145); color: oklch(72% 0.15 145); }

.logos { padding: 40px 0 56px; border-top: 1px solid var(--border); }
.logos-label { text-align: center; font-size: 12.5px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: var(--text-3); margin-bottom: 28px; }
.logos-row { display: flex; align-items: center; justify-content: center; gap: 48px; flex-wrap: wrap; }
.logo-item { font-size: 16px; font-weight: 700; color: var(--border-strong); letter-spacing: -0.5px; transition: color 0.15s; cursor: default; }
.logo-item:hover { color: var(--text-3); }

.section { padding: 96px 0; }
.section-alt { background: var(--bg-2); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
.section-label { display: inline-block; font-size: 12px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: var(--accent); margin-bottom: 14px; }
.section-title { font-size: clamp(28px, 3.5vw, 42px); font-weight: 800; letter-spacing: -1.2px; line-height: 1.1; color: var(--text); margin-bottom: 18px; text-wrap: balance; max-width: 560px; }
.section-sub { font-size: 16.5px; color: var(--text-3); max-width: 500px; line-height: 1.65; text-wrap: pretty; }

.features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; margin-top: 56px; background: var(--border); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
.feature-card { background: #fff; padding: 32px; display: flex; flex-direction: column; gap: 14px; transition: background 0.15s; }
.feature-card:hover { background: oklch(99.5% 0.005 260); }
.feature-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
.feature-title { font-size: 16px; font-weight: 700; letter-spacing: -0.3px; color: var(--text); }
.feature-body { font-size: 14px; color: var(--text-3); line-height: 1.65; }

.steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; margin-top: 56px; position: relative; }
.steps::before { content: ""; position: absolute; top: 20px; left: calc(12.5% + 20px); right: calc(12.5% + 20px); height: 1px; background: var(--border); z-index: 0; }
.step { display: flex; flex-direction: column; gap: 14px; position: relative; z-index: 1; }
.step-num { width: 40px; height: 40px; border-radius: 99px; background: #fff; border: 1.5px solid var(--border); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: var(--accent); font-family: 'JetBrains Mono', monospace; box-shadow: 0 1px 4px oklch(0% 0 0 / 0.06); }
.step-title { font-size: 15px; font-weight: 700; letter-spacing: -0.2px; color: var(--text); }
.step-body { font-size: 13.5px; color: var(--text-3); line-height: 1.6; }

.stats { padding: 64px 0; }
.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
.stat-cell { background: #fff; padding: 36px 32px; }
.stat-num { font-size: 44px; font-weight: 800; letter-spacing: -2px; color: var(--text); font-family: 'JetBrains Mono', monospace; line-height: 1; }
.stat-num span { font-size: 28px; }
.stat-label { font-size: 13.5px; color: var(--text-3); margin-top: 8px; line-height: 1.5; }

.testimonials { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 56px; }
.testimonial { background: #fff; border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 28px; display: flex; flex-direction: column; gap: 20px; }
.testimonial-quote { font-size: 15px; color: var(--text-2); line-height: 1.7; font-style: italic; }
.testimonial-quote::before { content: "\\201C"; color: var(--accent); font-size: 24px; line-height: 0; vertical-align: -8px; margin-right: 3px; font-style: normal; }
.testimonial-author { display: flex; align-items: center; gap: 12px; }
.testimonial-avatar { width: 36px; height: 36px; border-radius: 99px; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0; }
.testimonial-name { font-size: 13.5px; font-weight: 700; color: var(--text); }
.testimonial-role { font-size: 12px; color: var(--text-3); }

.cta-section { padding: 96px 0; text-align: center; }
.cta-card { background: var(--text); border-radius: 20px; padding: 72px 40px; position: relative; overflow: hidden; }
.cta-card::before { content: ""; position: absolute; inset: 0; background: radial-gradient(ellipse at 30% 50%, oklch(52% 0.22 265 / 0.35) 0%, transparent 60%), radial-gradient(ellipse at 70% 50%, oklch(55% 0.15 195 / 0.2) 0%, transparent 60%); }
.cta-inner { position: relative; z-index: 1; }
.cta-title { font-size: clamp(28px, 4vw, 48px); font-weight: 800; letter-spacing: -1.5px; color: #fff; line-height: 1.1; margin-bottom: 16px; text-wrap: balance; }
.cta-sub { font-size: 17px; color: oklch(70% 0.02 260); margin-bottom: 36px; max-width: 480px; margin-left: auto; margin-right: auto; }
.btn-white { padding: 13px 28px; border-radius: 8px; background: #fff; color: var(--text); font-size: 15px; font-weight: 700; border: none; cursor: pointer; font-family: inherit; transition: transform 0.15s, box-shadow 0.15s; display: inline-flex; align-items: center; gap: 8px; }
.btn-white:hover { transform: translateY(-1px); box-shadow: 0 8px 24px oklch(0% 0 0 / 0.25); }

footer { border-top: 1px solid var(--border); padding: 48px 0 40px; }
.footer-inner { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 40px; }
.footer-brand { display: flex; flex-direction: column; gap: 14px; }
.footer-tagline { font-size: 13.5px; color: var(--text-3); line-height: 1.6; max-width: 240px; }
.footer-col-title { font-size: 12px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: var(--text-2); margin-bottom: 14px; }
.footer-links { display: flex; flex-direction: column; gap: 10px; }
.footer-link { font-size: 13.5px; color: var(--text-3); transition: color 0.12s; cursor: pointer; }
.footer-link:hover { color: var(--text); }
.footer-bottom { margin-top: 48px; padding-top: 24px; border-top: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; font-size: 12.5px; color: var(--text-3); }
.mono { font-family: 'JetBrains Mono', monospace; }

.kw { color: oklch(72% 0.18 265); }
.fn { color: oklch(78% 0.14 195); }
.str { color: oklch(74% 0.13 80); }
.cm { color: oklch(48% 0.02 260); font-style: italic; }
.num { color: oklch(72% 0.14 145); }

@media (max-width: 900px) {
  .features-grid { grid-template-columns: 1fr; }
  .steps { grid-template-columns: 1fr 1fr; }
  .steps::before { display: none; }
  .testimonials { grid-template-columns: 1fr; }
  .stats-grid { grid-template-columns: 1fr 1fr; }
  .footer-inner { grid-template-columns: 1fr 1fr; }
  .ide-body { grid-template-columns: 1fr; }
  .ide-panel { display: none; }
}
@media (max-width: 600px) {
  .container { padding: 0 20px; }
  .hero { padding: 64px 0 48px; }
  .nav-links { display: none; }
  .steps { grid-template-columns: 1fr; }
  .stats-grid { grid-template-columns: 1fr; }
  .footer-inner { grid-template-columns: 1fr; }
}
`;

const Landing: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <style>{css}</style>

      {/* Nav */}
      <nav>
        <div className="container">
          <div className="nav-inner">
            <div className="nav-logo" onClick={() => navigate('/')}>
              <div className="nav-logo-mark">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              </div>
              <span className="nav-logo-name">TuringScout</span>
            </div>
            <div className="nav-links">
              <a href="#features" className="nav-link">Features</a>
              <a href="#how" className="nav-link">How it works</a>
              <a href="#results" className="nav-link">Results</a>
              <a href="#pricing" className="nav-link">Pricing</a>
            </div>
            <div className="nav-actions">
              <button className="btn-ghost" onClick={() => navigate('/login')}>Sign in</button>
              <button className="btn-primary" onClick={() => navigate('/login')}>Get started →</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-eyebrow">
            <div className="hero-eyebrow-dot"></div>
            Built for the AI-native engineering era
          </div>
          <h1>Hire engineers who<br/><em>actually</em> thrive with AI</h1>
          <p className="hero-sub">TuringScout gives technical recruiters a real assessment environment — not a quiz. Watch candidates build real code, with AI tools in hand, and measure what actually matters.</p>
          <div className="hero-actions">
            <button className="btn-primary btn-primary-lg" onClick={() => navigate('/login')}>Start evaluating candidates →</button>
            <a href="#how" className="btn-secondary-lg">See how it works</a>
          </div>
          <div className="hero-social">
            <div className="hero-avatars">
              <div className="hero-avatar" style={{background:'oklch(52% 0.22 265)', zIndex:4, marginLeft:0}}>SC</div>
              <div className="hero-avatar" style={{background:'oklch(55% 0.15 195)', zIndex:3}}>DP</div>
              <div className="hero-avatar" style={{background:'oklch(55% 0.18 295)', zIndex:2}}>EW</div>
              <div className="hero-avatar" style={{background:'oklch(68% 0.16 75)', zIndex:1}}>TN</div>
            </div>
            <span style={{marginLeft:6}}>Trusted by <strong style={{color:'var(--text-2)'}}>400+ recruiters</strong> at fast-growing engineering teams</span>
          </div>

          {/* IDE Preview */}
          <div className="ide-preview">
            <div className="ide-preview-glow"></div>
            <div className="ide-window">
              <div className="ide-titlebar">
                <div className="ide-dot" style={{background:'#ff5f57'}}></div>
                <div className="ide-dot" style={{background:'#febc2e'}}></div>
                <div className="ide-dot" style={{background:'#28c840'}}></div>
                <div className="ide-tab">challenge.py</div>
                <div className="ide-tab active">solution.py</div>
                <div className="ide-tab">tests.py</div>
                <div style={{flex:1}}></div>
                <div style={{fontSize:11, fontFamily:"'JetBrains Mono',monospace", color:'oklch(45% 0.02 260)', padding:'0 8px'}}>Priya Patel · Senior Backend · 00:41:22</div>
              </div>
              <div className="ide-body">
                <div className="ide-code">
                  <span className="cm"># TuringScout Assessment — Graph Pathfinding Challenge</span><br/>
                  <span className="cm"># AI tools: enabled &nbsp;|&nbsp; Time remaining: 18:38</span><br/>
                  <br/>
                  <span className="kw">import</span> heapq<br/>
                  <span className="kw">from</span> typing <span className="kw">import</span> Optional, List<br/>
                  <br/>
                  <span className="kw">def</span> <span className="fn">find_optimal_path</span>(<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;graph: dict[str, list[tuple[str, int]]],<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;start: str,<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;end: str<br/>
                  ) -&gt; Optional[List[str]]:<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="str">"""Find shortest path via modified Dijkstra."""</span><br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;heap = [(<span className="num">0</span>, start, [start])]<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;visited: set[str] = <span className="fn">set</span>()<br/>
                  <br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="kw">while</span> heap:<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cost, node, path = heapq.<span className="fn">heappop</span>(heap)<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="kw">if</span> node <span className="kw">in</span> visited: <span className="kw">continue</span><br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;visited.<span className="fn">add</span>(node)<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="kw">if</span> node == end: <span className="kw">return</span> path
                </div>
                <div className="ide-panel">
                  <div className="ide-panel-head">Live Assessment Metrics</div>
                  <div className="metric-row">
                    <span className="metric-label">code_quality</span>
                    <div className="metric-bar-wrap"><div className="metric-bar" style={{width:'88%',background:'oklch(60% 0.15 145)'}}></div></div>
                    <span className="metric-val" style={{color:'oklch(60% 0.15 145)'}}>88</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">test_coverage</span>
                    <div className="metric-bar-wrap"><div className="metric-bar" style={{width:'76%',background:'oklch(68% 0.16 75)'}}></div></div>
                    <span className="metric-val" style={{color:'oklch(68% 0.16 75)'}}>76</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">complexity</span>
                    <div className="metric-bar-wrap"><div className="metric-bar" style={{width:'82%',background:'oklch(60% 0.15 145)'}}></div></div>
                    <span className="metric-val" style={{color:'oklch(60% 0.15 145)'}}>82</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">documentation</span>
                    <div className="metric-bar-wrap"><div className="metric-bar" style={{width:'91%',background:'oklch(60% 0.15 145)'}}></div></div>
                    <span className="metric-val" style={{color:'oklch(60% 0.15 145)'}}>91</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">ai_utilisation</span>
                    <div className="metric-bar-wrap"><div className="metric-bar" style={{width:'63%',background:'oklch(55% 0.15 195)'}}></div></div>
                    <span className="metric-val" style={{color:'oklch(55% 0.15 195)'}}>63</span>
                  </div>
                  <div className="ide-score">
                    <div>
                      <div className="ide-score-num">87</div>
                      <div className="ide-score-label" style={{marginTop:4}}>OVERALL SCORE</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div className="ide-badge">✓ PASS</div>
                      <div style={{fontSize:11,color:'oklch(45% 0.02 260)',marginTop:6,fontFamily:"'JetBrains Mono',monospace"}}>1h 23m taken</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo bar */}
      <div className="logos">
        <div className="container">
          <div className="logos-label">Trusted by engineering teams at</div>
          <div className="logos-row">
            <span className="logo-item">Acme Corp</span>
            <span className="logo-item">Meridian AI</span>
            <span className="logo-item">Stackform</span>
            <span className="logo-item">Helix Labs</span>
            <span className="logo-item">Northvault</span>
            <span className="logo-item">Cinderblock</span>
          </div>
        </div>
      </div>

      {/* Features */}
      <section className="section" id="features">
        <div className="container">
          <div>
            <div className="section-label">Features</div>
            <div className="section-title">Assessment infrastructure built for the real world</div>
            <p className="section-sub">Most coding tests measure whether candidates can Google answers. TuringScout measures something harder — and more valuable.</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon" style={{background:'var(--accent-light)'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              </div>
              <div className="feature-title">Full IDE environment</div>
              <div className="feature-body">Candidates work in a real, browser-based IDE — not a stripped-down text box. They get autocomplete, file trees, and terminal access, just like the job.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon" style={{background:'oklch(95% 0.05 195)'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
              </div>
              <div className="feature-title">AI tools, intentionally enabled</div>
              <div className="feature-body">Candidates can use AI assistants — because your engineers do too. TuringScout scores <em>how well</em> they leverage AI, not just whether they do.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon" style={{background:'oklch(95% 0.05 145)'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              </div>
              <div className="feature-title">Multi-dimensional scoring</div>
              <div className="feature-body">Code quality, test coverage, documentation, complexity, and AI utilisation — each scored independently and rolled into a single recruiter-readable summary.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon" style={{background:'oklch(97% 0.05 75)'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div className="feature-title">Fixed templates or custom prompts</div>
              <div className="feature-body">Choose from a curated library of challenges by role and level, or write your own prompt for fully bespoke assessments tailored to your stack.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon" style={{background:'oklch(95% 0.05 295)'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="oklch(55% 0.18 295)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <div className="feature-title">One-click candidate invites</div>
              <div className="feature-body">Enter a name and email — TuringScout generates a time-limited, personalised assessment link and delivers it instantly. No scheduling, no setup calls.</div>
            </div>
            <div className="feature-card">
              <div className="feature-icon" style={{background:'var(--accent-light)'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <div className="feature-title">AWS-native infrastructure</div>
              <div className="feature-body">Every session runs in an isolated ECS container. Submissions are stored in S3 and DynamoDB. Enterprise-grade reliability with no vendor lock-in on your data.</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section section-alt" id="how">
        <div className="container">
          <div className="section-label">How it works</div>
          <div className="section-title">From invite to insight in four steps</div>
          <div className="steps">
            <div className="step">
              <div className="step-num">01</div>
              <div className="step-title">Invite the candidate</div>
              <div className="step-body">Enter their name and email, pick a challenge template or write a custom prompt. TuringScout sends the link — your job is done until results come in.</div>
            </div>
            <div className="step">
              <div className="step-num">02</div>
              <div className="step-title">Candidate completes the session</div>
              <div className="step-body">They work in a full browser IDE with AI tools available. Sessions are time-boxed and isolated — no collaboration, no pre-loading answers.</div>
            </div>
            <div className="step">
              <div className="step-num">03</div>
              <div className="step-title">Automated scoring runs</div>
              <div className="step-body">The moment they submit, TuringScout analyses the code across five dimensions. Results are available before you've had your next coffee.</div>
            </div>
            <div className="step">
              <div className="step-num">04</div>
              <div className="step-title">Review and decide</div>
              <div className="step-body">A structured results page shows scores, code preview, and pass/fail. Share a link with the hiring manager or push to your ATS integration.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats" id="results">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-cell">
              <div className="stat-num">91<span>%</span></div>
              <div className="stat-label">of hiring managers say TuringScout results match 90-day on-the-job performance</div>
            </div>
            <div className="stat-cell">
              <div className="stat-num">3.4<span>×</span></div>
              <div className="stat-label">faster time-to-offer compared to traditional multi-stage technical screens</div>
            </div>
            <div className="stat-cell">
              <div className="stat-num">48<span>k</span></div>
              <div className="stat-label">assessments completed across 400+ recruiting teams and counting</div>
            </div>
            <div className="stat-cell">
              <div className="stat-num">&lt;2<span>m</span></div>
              <div className="stat-label">average time from session submission to full scored results in the dashboard</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-label">From recruiters</div>
          <div className="section-title">The signal we've been missing</div>
          <div className="testimonials">
            <div className="testimonial">
              <div className="testimonial-quote">We used to spend three rounds of interviews finding out a candidate couldn't actually ship code. TuringScout tells us that in the first hour, before we've invested a single engineering hour.</div>
              <div className="testimonial-author">
                <div className="testimonial-avatar" style={{background:'var(--accent)'}}>KL</div>
                <div>
                  <div className="testimonial-name">Kira Langford</div>
                  <div className="testimonial-role">Head of Engineering Talent · Stackform</div>
                </div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-quote">The AI utilisation metric is what sold us. We're hiring for an AI-first team — knowing how a candidate actually uses these tools is more predictive than any LeetCode score.</div>
              <div className="testimonial-author">
                <div className="testimonial-avatar" style={{background:'var(--teal)'}}>MR</div>
                <div>
                  <div className="testimonial-name">Marcus Reyes</div>
                  <div className="testimonial-role">Technical Recruiter · Meridian AI</div>
                </div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-quote">Setup took less than a day. Our first cohort of candidates went through it the same week. The results page is clean enough that our non-technical execs can read it without any explanation.</div>
              <div className="testimonial-author">
                <div className="testimonial-avatar" style={{background:'oklch(55% 0.18 295)'}}>AH</div>
                <div>
                  <div className="testimonial-name">Aisha Hassan</div>
                  <div className="testimonial-role">People Operations Lead · Helix Labs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" id="pricing">
        <div className="container">
          <div className="cta-card">
            <div className="cta-inner">
              <div className="cta-title">Ready to hire engineers<br/>who can actually build?</div>
              <div className="cta-sub">Start evaluating candidates today. No setup calls. No contract required to begin.</div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:14,flexWrap:'wrap'}}>
                <button className="btn-white" onClick={() => navigate('/login')}>Get started — it's free to try →</button>
                <span style={{fontSize:14,color:'oklch(55% 0.02 260)'}}>or <button onClick={() => navigate('/login')} style={{color:'oklch(72% 0.1 265)',textDecoration:'underline',textUnderlineOffset:3,background:'none',border:'none',cursor:'pointer',fontSize:14}}>sign in to your account</button></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="footer-inner">
            <div className="footer-brand">
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:28,height:28,borderRadius:7,background:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                </div>
                <span style={{fontWeight:700,fontSize:14,letterSpacing:'-0.3px'}}>TuringScout</span>
              </div>
              <p className="footer-tagline">Assessment infrastructure for engineering teams hiring in the AI era. Built on AWS ECS, S3, and DynamoDB.</p>
            </div>
            <div>
              <div className="footer-col-title">Product</div>
              <div className="footer-links">
                <a href="#features" className="footer-link">Features</a>
                <a href="#how" className="footer-link">How it works</a>
                <a href="#pricing" className="footer-link">Pricing</a>
                <a href="/#" className="footer-link">Changelog</a>
              </div>
            </div>
            <div>
              <div className="footer-col-title">Company</div>
              <div className="footer-links">
                <a href="/#" className="footer-link">About</a>
                <a href="/#" className="footer-link">Blog</a>
                <a href="/#" className="footer-link">Careers</a>
                <a href="/#" className="footer-link">Contact</a>
              </div>
            </div>
            <div>
              <div className="footer-col-title">Legal</div>
              <div className="footer-links">
                <a href="/#" className="footer-link">Privacy Policy</a>
                <a href="/#" className="footer-link">Terms of Service</a>
                <a href="/#" className="footer-link">Security</a>
                <a href="/#" className="footer-link">GDPR</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© 2026 TuringScout. All rights reserved.</span>
            <span className="mono" style={{fontSize:11.5}}>v1.0.0 · Hosted on AWS ECS · us-east-1</span>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Landing;
